<script setup>
import {
  ref,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { ATTACHMENT_TYPES } from '../constants';
import FormattedContent from './Text/FormattedContent.vue';
import { detectMediaTones } from './mediaTone';
import {
  classifyMediaAspectRatio,
  mediaFrameStyle,
  mediaAssetClass,
} from './mediaAspectLayout';

const emit = defineEmits(['error']);
const hasError = ref(false);
const showGallery = ref(false);
const videoRef = ref(null);
const duration = ref(0);
const detectedAspectRatio = ref('');
const isPlaying = ref(false);
const senderTone = ref('light');
const chevronTone = ref('light');
const timeTone = ref('light');
const {
  filteredCurrentChatAttachments,
  attachments,
  content,
  contentAttributes,
} = useMessageContext();

const captionExpanded = ref(false);
const caption = computed(() => {
  let value = String(content.value || '').trim();
  const normalizedValue = value.toLowerCase();
  const technicalMediaLabels = [
    'mensagem recebida',
    '[mensagem recebida]',
    'message received',
    '[message received]',
    'mídia recebida',
    '[mídia recebida]',
    'media received',
    '[media received]',
  ];

  if (technicalMediaLabels.includes(normalizedValue)) return '';

  // Aceleres: quando o rotulo tecnico vem colado como PREFIXO do texto real
  // (ex.: "[Mensagem recebida]\n\ntexto"), o match exato acima nao pega e
  // sobra o rotulo + linhas em branco antes da legenda, parecendo um vao
  // vazio na bolha. Corta so o prefixo tecnico nesse caso.
  const matchedLabel = technicalMediaLabels.find(label =>
    normalizedValue.startsWith(label)
  );
  if (matchedLabel) {
    value = value.slice(matchedLabel.length).trim();
  }

  return value;
});

const hasCaption = computed(() => Boolean(caption.value));
const hasMoreCaption = computed(() => caption.value.length > 768);
const visibleCaption = computed(() => {
  const limitedCaption = caption.value.slice(0, 1024);
  if (!captionExpanded.value && hasMoreCaption.value) {
    return `${limitedCaption.slice(0, 768)}...`;
  }

  return limitedCaption;
});

const handleError = () => {
  hasError.value = true;
  emit('error');
};

const attachment = computed(() => {
  return attachments.value[0];
});

const isReel = computed(() => {
  return attachment.value.fileType === ATTACHMENT_TYPES.IG_REEL;
});

const isGif = computed(
  () =>
    String(
      contentAttributes.value?.wameMediaType ||
        contentAttributes.value?.wame_media_type ||
        ''
    ).toLowerCase() === 'gif'
);

const mediaAspectRatio = computed(() => {
  if (detectedAspectRatio.value) return detectedAspectRatio.value;

  const { width, height } = attachment.value || {};
  if (Number(width) > 0 && Number(height) > 0) {
    return `${width} / ${height}`;
  }
  return isReel.value ? '9 / 16' : '16 / 9';
});

const detectedNumericAspectRatio = ref(null);

const numericAspectRatio = computed(() => {
  if (detectedNumericAspectRatio.value) return detectedNumericAspectRatio.value;

  const { width, height } = attachment.value || {};
  return Number(width) > 0 && Number(height) > 0
    ? Number(width) / Number(height)
    : null;
});

// Aceleres: classificacao pelo aspect ratio real (nunca pela altura em
// pixels isolada), igual ao WhatsApp — compartilhada com Image.vue. GIF e
// reel mantem o comportamento antigo (aspect-ratio natural, sem limites).
const videoAspectClass = computed(() => {
  if (isGif.value || isReel.value) return null;
  return classifyMediaAspectRatio(numericAspectRatio.value);
});

const playerStyle = computed(() => {
  if (!videoAspectClass.value) return { aspectRatio: mediaAspectRatio.value };
  return mediaFrameStyle(videoAspectClass.value);
});

const videoAssetClass = computed(() => {
  if (!videoAspectClass.value) return 'w-full h-full object-cover';
  return mediaAssetClass(videoAspectClass.value);
});

const durationLabel = computed(() => {
  if (!Number.isFinite(duration.value) || duration.value <= 0) return '';
  const totalSeconds = Math.floor(duration.value);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`
    : `${minutes}:${seconds}`;
});

const mediaDurationLabel = computed(() =>
  isGif.value ? '' : durationLabel.value
);

// Aceleres: GIF do WhatsApp chega como MP4 mudo. Ele nao se comporta como
// video (sem play, sem duracao): roda sozinho 3 vezes e para, mostrando o
// botao redondo "GIF" no centro — tocar nele roda outras 3 vezes.
const GIF_MAX_LOOPS = 3;
const gifLoops = ref(0);
const gifFinished = ref(false);

const handleGifEnded = () => {
  const video = videoRef.value;
  gifLoops.value += 1;
  if (gifLoops.value >= GIF_MAX_LOOPS) {
    gifFinished.value = true;
    return;
  }
  if (video) {
    video.currentTime = 0;
    video.play().catch(() => {});
  }
};

const handleEnded = () => {
  isPlaying.value = false;
  if (isGif.value) handleGifEnded();
};

const replayGif = () => {
  const video = videoRef.value;
  if (!video) return;
  gifLoops.value = 0;
  gifFinished.value = false;
  video.currentTime = 0;
  video.play().catch(() => {});
};

// Aceleres: o GIF so roda quando entra na tela. Antes ele comecava no load,
// entao uma conversa com varios GIFs gastava as 3 voltas de todos enquanto
// ainda estavam fora da area visivel — ao rolar ate eles, ja estavam parados
// no botao "GIF". Sair da tela pausa; voltar recomeca a contagem.
let gifObserver = null;

const stopGifObserver = () => {
  if (!gifObserver) return;
  gifObserver.disconnect();
  gifObserver = null;
};

const startGifObserver = () => {
  stopGifObserver();
  const video = videoRef.value;
  if (!isGif.value || !video || typeof IntersectionObserver === 'undefined') {
    return;
  }

  gifObserver = new IntersectionObserver(
    entries => {
      const entry = entries[0];
      if (!entry) return;
      if (entry.isIntersecting) {
        replayGif();
        return;
      }
      videoRef.value?.pause?.();
    },
    { threshold: 0.35 }
  );
  gifObserver.observe(video);
};

onMounted(() => {
  // O <video> so existe depois do render; o watch cobre o caso do GIF ser
  // reconhecido depois (metadata/atributos chegando em seguida).
  nextTick(startGifObserver);
});
watch(isGif, startGifObserver);
onBeforeUnmount(stopGifObserver);

const handleLoadedMetadata = async event => {
  const video = event.currentTarget;
  duration.value = Number(video?.duration) || 0;

  if (Number(video?.videoWidth) > 0 && Number(video?.videoHeight) > 0) {
    detectedAspectRatio.value = `${video.videoWidth} / ${video.videoHeight}`;
    detectedNumericAspectRatio.value = video.videoWidth / video.videoHeight;
  }

  await nextTick();
  if (isGif.value) {
    video.muted = true;
    // Sem `loop`: a contagem das 3 repeticoes depende do evento `ended`,
    // que o proprio `loop` impediria de disparar.
    video.loop = false;
    gifLoops.value = 0;
    gifFinished.value = false;
    // Nao chama play() aqui: quem inicia e o IntersectionObserver, quando o
    // GIF aparece na tela.
    startGifObserver();
  }
};

const detectVideoTones = event => {
  try {
    const tones = detectMediaTones(event.currentTarget);
    senderTone.value = tones.sender;
    chevronTone.value = tones.chevron;
    timeTone.value = tones.time;
  } catch {
    senderTone.value = 'light';
    chevronTone.value = 'light';
    timeTone.value = 'light';
  }
};

const openVideoGallery = () => {
  showGallery.value = true;
};
</script>

<template>
  <BaseBubble
    class="acl-video-bubble overflow-hidden p-1.5"
    :class="{
      'acl-video-bubble--playing': isPlaying,
      'acl-video-bubble--reel': isReel,
      'acl-video-bubble--gif': isGif,
    }"
    :data-bubble-name="isGif ? 'gif' : 'video'"
    :data-aspect-class="videoAspectClass || undefined"
    :data-sender-tone="senderTone"
    :data-chevron-tone="chevronTone"
    :data-time-tone="timeTone"
    media-caption
    show-sender-name
    role="button"
    tabindex="0"
    @click="showGallery = true"
    @keydown.enter.space.self.prevent="showGallery = true"
  >
    <div
      class="acl-video-bubble__player relative overflow-hidden max-h-[420px]"
      :class="{ 'acl-video-bubble__player--gif': isGif }"
      :style="playerStyle"
      @pointerdown.prevent.stop="openVideoGallery"
      @click.prevent.stop="openVideoGallery"
      @dblclick.stop="showGallery = true"
    >
      <div
        v-if="isReel"
        class="absolute right-0 z-[4] flex items-start justify-end p-2 pointer-events-none"
      >
        <Icon icon="i-lucide-instagram" class="text-white shadow-lg" />
      </div>
      <video
        ref="videoRef"
        :muted="isGif"
        :poster="attachment.thumbUrl || undefined"
        playsinline
        preload="metadata"
        class="acl-video-bubble__asset skip-context-menu block"
        :class="videoAssetClass"
        :src="attachment.dataUrl"
        @pointerdown.prevent.stop="openVideoGallery"
        @click.prevent.stop="openVideoGallery"
        @error="handleError"
        @loadeddata="detectVideoTones"
        @loadedmetadata="handleLoadedMetadata"
        @play="isPlaying = true"
        @pause="isPlaying = false"
        @ended="handleEnded"
      />

      <!-- Aceleres: fim das 3 repeticoes do GIF — botao redondo escuro no
           centro, com "GIF" em branco. Tocar nele roda mais 3 vezes. -->
      <button
        v-if="isGif && gifFinished"
        type="button"
        aria-label="Reproduzir GIF novamente"
        class="acl-video-bubble__gif-replay absolute inset-0 z-[4] flex items-center justify-center border-0 bg-transparent p-0"
        @pointerdown.prevent.stop="replayGif"
        @click.prevent.stop="replayGif"
      >
        <span class="flex items-center justify-center"> GIF </span>
      </button>

      <button
        v-if="!isGif"
        type="button"
        class="acl-video-bubble__play absolute inset-0 z-[3] flex items-center justify-center border-0 bg-transparent p-0"
        @pointerdown.prevent.stop="openVideoGallery"
        @click.prevent.stop="openVideoGallery"
      >
        <!-- Aceleres: sem essas classes o botao/circulo ficava em fluxo
             normal (nao sobreposto ao video), empurrando a altura do frame
             pra baixo do video real e deixando um vao vazio antes do
             horario — corrigido posicionando como overlay absoluto. -->
        <span class="acl-video-bubble__play-circle flex size-12 items-center justify-center rounded-full bg-black/40 text-white">
          <Icon icon="i-lucide-play" class="size-7" />
        </span>
      </button>

      <!-- Aceleres: no GIF o rodape nao tem camera nem duracao — leva a
           marca "GIPHY". Branco com sombra, igual ao horario: a marca fica
           por cima da propria animacao, onde cinza sumia. -->
      <span
        v-if="isGif"
        class="acl-video-bubble__giphy absolute left-2 bottom-2 z-[3] text-[12px] font-semibold tracking-wide"
      >
        GIPHY
      </span>

      <span
        v-if="mediaDurationLabel"
        class="acl-video-bubble__duration absolute left-2 bottom-2 z-[3] flex items-center gap-1 rounded-full bg-black/45 px-1.5 py-0.5 text-[12px] font-medium text-white"
      >
        <svg
          viewBox="0 0 16 14"
          height="14"
          width="16"
          preserveAspectRatio="xMidYMid meet"
          version="1.1"
          x="0px"
          y="0px"
          enable-background="new 0 0 16 14"
          aria-hidden="true"
        >
          <title>msg-video</title>
          <path
            fill="currentColor"
            d="M14.99 2.67 11.5 5.76V3.49a1.2 1.2 0 0 0-1.2-1.19H1.7c-.67 0-1.2.53-1.2 1.19v7.14c0 .65.53 1.19 1.19 1.19h8.63a1.2 1.2 0 0 0 1.19-1.2v-2.3l3.48 3.1V2.67z"
          />
        </svg>
        {{ mediaDurationLabel }}
      </span>
    </div>
    <div
      v-if="hasCaption"
      class="acl-video-bubble__caption box-border w-full min-h-9 px-2 pb-6 pt-2 text-[15.8px] leading-5"
      @click.stop
    >
      <span class="inline">
        <FormattedContent
          class="acl-message-caption [&_p]:!inline [&_p]:!m-0"
          :content="visibleCaption"
        />
        <button
          v-if="hasMoreCaption && !captionExpanded"
          type="button"
          class="inline font-bold text-[#1b8755] no-underline -ml-px !m-0 !p-0 border-0 leading-none"
          @click.stop="captionExpanded = true"
        >
          Ler mais
        </button>
      </span>
    </div>
    <slot name="menu-trigger" :chevron-tone="chevronTone" />
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    :initial-caption="caption"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>
